import type { Placement } from '@popperjs/core';
import type { ButtonSize } from '@timelyapp/react';
import { Checkbox, Icon } from '@timelyapp/react';
import classNames from 'classnames';
import ClearableButton from 'components/buttons/ClearableButton';
import Popover from 'components/popovers/Popover';
import { appIcons } from 'constants/known-apps.icons';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getConnectedTicIntegrationsWithReferenceTasks,
  getTicIntegrationIcons,
} from 'redux/modules/tic/tic.integrations.selectors';
import styles from './LinkedWorkSyncFilterDropdown.module.scss';

export const LINKED_WORK_SYNC_FILTER_DROPDOWN_TEST_ID = 'linked-work-sync-filter-dropdown';

interface Props {
  selectedExternalLinkFilters?: string[];
  selectedSyncStatusFilters?: string[];
  onChange: (externalLinkFilters?: string[], syncStatusFilters?: string[]) => void;
  size?: ButtonSize;
  position?: Placement;
  fixed?: boolean;
  disabled?: boolean;
  clearTooltip?: string;
  testId?: string;
}

interface FilterOption {
  value: string;
  label: string;
  type: 'link' | 'sync' | 'divider' | 'header';
  image?: string;
  name?: string;
}

const getIntegrationLabel = (integration: string): string => {
  const labelMap: Record<string, string> = {
    jira: 'Jira issue',
    clickup: 'ClickUp task',
    monday: 'Monday item',
    asana: 'Asana task',
    asana_sync: 'Asana task',
    linear: 'Linear issue',
  };
  return (
    labelMap[integration] || `${integration.charAt(0).toUpperCase() + integration.slice(1)} item`
  );
};

const getIntegrationIcon = (
  integration: string,
  ticIcons: Record<string, string | null>
): string | undefined => {
  // Priority: TIC integration icons from API, then legacy appIcons
  return ticIcons[integration] || appIcons[integration] || undefined;
};

const getIntegrationName = (integration: string): string => {
  const nameMap: Record<string, string> = {
    jira: 'Jira',
    clickup: 'ClickUp',
    monday: 'Monday',
    asana: 'Asana',
    asana_sync: 'Asana',
    linear: 'Linear',
  };
  return nameMap[integration] || integration;
};

const LinkedWorkSyncFilterDropdown: React.FC<Props> = ({
  selectedExternalLinkFilters = [],
  selectedSyncStatusFilters = [],
  onChange,
  size = 'medium',
  position = 'bottom',
  fixed: _fixed,
  disabled,
  clearTooltip,
  testId = LINKED_WORK_SYNC_FILTER_DROPDOWN_TEST_ID,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const connectedIntegrations = useSelector(getConnectedTicIntegrationsWithReferenceTasks);
  const ticIntegrationIcons = useSelector(getTicIntegrationIcons);

  const options: FilterOption[] = useMemo(() => {
    const opts: FilterOption[] = [];

    // Linked work item section header
    opts.push({ value: 'header_link', label: 'Linked work item', type: 'header' });

    // Add provider-specific options for connected integrations
    // TIC controls which integrations support linked work items via reference_tasks setting
    connectedIntegrations.forEach(integration => {
      const integrationLower = integration.toLowerCase();
      opts.push({
        value: `provider:${integrationLower}`,
        label: `Linked to ${getIntegrationLabel(integrationLower)}`,
        type: 'link',
        image: getIntegrationIcon(integrationLower, ticIntegrationIcons),
        name: getIntegrationName(integrationLower),
      });
    });

    // No linked work option
    opts.push({ value: 'without_links', label: 'No linked work', type: 'link' });

    // Add divider
    opts.push({ value: 'divider', label: '', type: 'divider' });

    // Sync status section header
    opts.push({ value: 'header_sync', label: 'Sync status to external system', type: 'header' });

    // Sync status options
    opts.push(
      { value: 'synced', label: 'Successfully synced', type: 'sync' },
      { value: 'needs_attention', label: 'Failed to sync', type: 'sync' },
      { value: 'without_status', label: 'No sync status', type: 'sync' }
    );

    return opts;
  }, [connectedIntegrations, ticIntegrationIcons]);

  const selectedCount = useMemo(() => {
    return selectedExternalLinkFilters.length + selectedSyncStatusFilters.length;
  }, [selectedExternalLinkFilters, selectedSyncStatusFilters]);

  const hasSelection = selectedCount > 0;

  const handleSelect = useCallback(
    (value: string | number | null) => {
      if (typeof value !== 'string') return;

      const option = options.find(opt => opt.value === value);
      if (!option) return;

      if (option.type === 'link') {
        // Toggle link filter in array, keep sync filters unchanged
        const newLinkFilters = selectedExternalLinkFilters.includes(value)
          ? selectedExternalLinkFilters.filter(f => f !== value)
          : [...selectedExternalLinkFilters, value];
        onChange(
          newLinkFilters.length > 0 ? newLinkFilters : undefined,
          selectedSyncStatusFilters.length > 0 ? selectedSyncStatusFilters : undefined
        );
      } else if (option.type === 'sync') {
        // Toggle sync filter in array, keep link filters unchanged
        const newSyncFilters = selectedSyncStatusFilters.includes(value)
          ? selectedSyncStatusFilters.filter(f => f !== value)
          : [...selectedSyncStatusFilters, value];
        onChange(
          selectedExternalLinkFilters.length > 0 ? selectedExternalLinkFilters : undefined,
          newSyncFilters.length > 0 ? newSyncFilters : undefined
        );
      }
    },
    [onChange, options, selectedExternalLinkFilters, selectedSyncStatusFilters]
  );

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      onChange(undefined, undefined);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div data-testid={testId} style={{ display: 'inline-block' }}>
      <Popover
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        position={position}
        zIndex={50}
        target={
          <ClearableButton
            priority="medium"
            size={size}
            data-testid={LINKED_WORK_SYNC_FILTER_DROPDOWN_TEST_ID}
            caret
            onClear={hasSelection ? handleClear : undefined}
            isActive={null}
            selected={hasSelection}
            clearTooltip={clearTooltip || 'Clear the linked work / sync filter'}
            disabled={disabled}
          >
            {hasSelection ? (
              <>{selectedCount} selected</>
            ) : (
              <>
                <Icon name="LinkExternal01" type="solid" />
                {'Linked work'}
              </>
            )}
          </ClearableButton>
        }
      >
        {() => (
          <div className={styles.container}>
            {options.map(option => {
              if (option.type === 'header') {
                return (
                  <div key={option.value} className={styles.header}>
                    {option.label}
                  </div>
                );
              }

              if (option.type === 'divider') {
                return <div key={option.value} className={styles.divider} />;
              }

              const isSelected =
                selectedExternalLinkFilters.includes(option.value) ||
                selectedSyncStatusFilters.includes(option.value);

              return (
                <div
                  key={option.value}
                  onClick={() => !disabled && handleSelect(option.value)}
                  className={classNames(styles.option, { [styles.disabled]: disabled })}
                >
                  <Checkbox readOnly checked={isSelected} disabled={disabled} />
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.image && (
                    <img src={option.image} alt={option.name} className={styles.optionIcon} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Popover>
    </div>
  );
};

export default LinkedWorkSyncFilterDropdown;
